// @flow

// Prerequisite price documentation https://elasticsuite.github.io/cookbook/prices

/**
 * MonetaryAmount
 *
 * A MonetaryAmount represents a quantity of currency as a number.
 *
 * This representation is notably missing units (the customer's currency code),
 * which is added when an amount is prepared for display.
 *
 * MonetaryAmounts are used in Scramble to represent unit prices,
 * discount amounts, freight charges, etc. They are also used to perform calculations
 * on these amounts and represent the resulting totals, such as the total cost of
 * 10 units of one size of a variation, or the total for an order.
 */

export type MonetaryAmount = number;

/**
 *MonetaryAmountRange
 *
 * A MonetaryAmountRange represents a range of amounts as a high and low number.
 *
 * A product can have multiple variations with different prices, and a variation can have multiple
 * stock items (sizes) with different prices. MonetaryAmountRanges are used in Scramble to
 * represent price ranges for both products and variations.
 *
 * While calculations are performed, the distinction between amounts and ranges
 * is preserved. When amounts are prepared for display, an amount will usually
 * be converted to a range, eg. amount -> { low: amount, high: amount } to normalize
 * output. Display components show only one amount when high === low.
 */

export type MonetaryAmountRange = $ReadOnly<{|
  low: MonetaryAmount,
  high: MonetaryAmount,
|}>;

/**
 * PriceDisplayPreference represents the user's preference for displaying
 * amounts of different purposes within the UI.
 *
 * This preference is used throughout the application to determine which amounts display.
 */

export type PriceDisplayPreference = 'wholesale' | 'retail' | 'both' | 'none';

/**
 * MonetaryAmountByPurpose
 *
 * A MonetaryAmountByPurpose represents a group of amounts that correspond
 * to the same item or same total, but are used for different purposes.
 *
 * Scramble uses wholesale to represent the price a customer building an order
 * on the Elastic platform pays and retail to represent the manufacturer's suggested retail price.
 *
 * Wholesale, or what should more rightly be referred to a buying price, can be meaningfully
 * totalled with freight, tax, discounts, and other amounts related to an order. By contrast,
 * MSRP is for reference only.
 *
 * While Elastic has historically supported only wholesale and retail, clients frequently
 * request support for more flexibility in displaying prices for other purposes. It seems
 * inevitable that support will be added.
 */

export type MonetaryAmountByPurpose = $ReadOnly<{|
  wholesale: MonetaryAmount,
  retail: MonetaryAmount | null,
|}>;

/**
 * MonetaryAmountRangeByPurpose
 *
 * A MonetaryAmountRangeByPurpose represents ranges for different purposes that correspond
 * to the same item or same total, combining the MonetaryAmountRange and MonetaryAmountByPurpose.
 * The same uses and caveats apply, so take the time to grok MonetaryAmountRange and
 * MonetaryAmountByPurpose before proceeding.
 *
 * Scramble uses MonetaryAmountRangeByPurpose to represent the buying and MSRP ranges (and
 * potentially ranges that serve other purposes) that correspond to the same item or total.
 *
 * It is worth noting that before display, MonetaryAmountByPurpose is converted to
 * MonetaryAmountRangeByPurpose.
 */

export type MonetaryAmountRangeByPurpose = $ReadOnly<{|
  wholesale: MonetaryAmountRange,
  retail: MonetaryAmountRange | null,
|}>;

/**
 * PriceGroupToMonetaryAmountMap
 *
 * A PriceGroupToMonetaryAmountMap represents unit prices of a stock item for any number of
 * wholesale- and retail- price groups.
 *
 * Scramble consumes PriceGroupToMonetaryAmountMaps from skillet at the variation stock item level.
 * Previously, it was commonplace to pass this type around scramble code—the evidence of that
 * mistake abounds, cluttering and confusing the codebase.
 *
 * Current practice is to use a price group to extract a monetary amount from the
 * PriceGroupToMonetaryAmountMap as a first step, and only then perform calculations
 */

export type PriceGroupToMonetaryAmountMap = { [price_group: string]: number };

/**
 * DisplayableMonetaryAmount
 *
 * Likely unneeded. Only MonetaryAmountRanges are used for display
 */

export type DisplayableMonetaryAmount = MonetaryAmount | MonetaryAmountRange;

/**
 * MonetaryAmountForDisplayFields
 *
 * Represents almost all of the information† needed to display a MontaryAmount,
 * excluding the amount itself.
 *
 * - `obsolete` represents a price that does not apply, usually indicated with a strikethrough
 * - `emphasize` represents a price that Quiksilver wants to draw attention to
 * - `currencyCode` provides the necessary unit to make the amount meaningful
 * - `isLoading` represents an amount that has been queried for, but is not yet loaded
 * - `error` usually represents an error with a loading amount, but could also represent
 * an error with totaling
 *
 * Scramble uses this information to display amounts in a way that helps users make sense of an
 * often overabundant volume of information about unit prices, totals, discounts, upcharges,
 * and other costs.
 *
 * † locale is pulled from context in the monetary amount label
 */

type MonetaryAmountForDisplayFields = $ReadOnly<{|
  obsolete?: boolean,
  emphasize?: boolean,
  currencyCode: string,
  isLoading?: boolean,
  error?: ?Error,
|}>;

/**
 * MonetaryAmountForDisplay
 *
 * Represents the combined amount and display infomation required to show the user a price
 *
 * The only use for MonetaryAmountForDisplay is to render the amount for display.
 * Further calculations are not appropriate.
 */

export type MonetaryAmountForDisplay = $ReadOnly<{|
  range: MonetaryAmountRange,
  ...MonetaryAmountForDisplayFields,
|}>;

/**
 * DEFAULT_AMOUNT_RANGE
 *
 * Represents a last resort, eg. when a variation has no stock items to derive a range from
 *
 * Used only in the most desperate of circumstances
 */

export const DEFAULT_AMOUNT_RANGE: MonetaryAmountRange = { low: 0, high: 0 };

export const rangesAreEqual = (
  { low: lowA, high: highA }: MonetaryAmountRange,
  { low: lowB, high: highB }: MonetaryAmountRange,
): boolean => lowA === lowB && highA === highB;

export const amountForGroup = (
  price: PriceGroupToMonetaryAmountMap,
  priceGroup: string,
): MonetaryAmount | null => {
  const amount = price[priceGroup];
  return amount ?? null;
};

export const shouldShowAmountsOfPurposeChecker =
  (
    amountPurposeDisplayPreference: PriceDisplayPreference,
    usesExternalPrices: boolean,
  ): ((purpose: 'wholesale' | 'retail' | 'external') => boolean) =>
  (purpose: 'wholesale' | 'retail' | 'external'): boolean => {
    if (!usesExternalPrices && purpose === 'external') return false;

    return (
      (purpose === 'external' &&
        amountPurposeDisplayPreference === 'wholesale') ||
      [purpose, 'both'].includes(amountPurposeDisplayPreference)
    );
  };
